import { useEffect, useState } from 'react';
import { Drawer, Skeleton } from '../../../ui';
import { useLead } from '../../../hooks/useLeads';
import { OverviewTab } from './tabs/OverviewTab';
import { ActivityTab } from './tabs/ActivityTab';
import { NotesTab } from './tabs/NotesTab';
import { EmailTab } from './tabs/EmailTab';

interface Props {
  leadId: number | null;
  open: boolean;
  onClose: () => void;
}

const TABS = [
  { id: 'overview', label: 'Overview' },
  { id: 'activity', label: 'Atividade' },
  { id: 'notes', label: 'Notas' },
  { id: 'email', label: 'Email' },
] as const;

type TabId = (typeof TABS)[number]['id'];

export function LeadDetailDrawer({ leadId, open, onClose }: Props) {
  const [tab, setTab] = useState<TabId>('overview');
  const { data: lead, isLoading } = useLead(leadId);

  // Reset tab when drawer opens for a different lead
  useEffect(() => {
    if (open) {
      setTab('overview');
    }
  }, [open, leadId]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={lead?.name ?? 'Lead'}
      description={lead?.company ?? undefined}
      width={520}
    >
      <div className="flex flex-col gap-4">
        <nav className="flex gap-2 border-b border-border -mx-5 px-5 sticky top-0 bg-surface z-10">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`text-sm py-2 px-3 -mb-px border-b-2 transition-colors ${
                tab === t.id
                  ? 'border-ink-primary text-ink-primary font-medium'
                  : 'border-transparent text-ink-tertiary hover:text-ink-secondary'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {isLoading || !lead ? (
          <Skeleton height={200} />
        ) : (
          <div>
            {tab === 'overview' && <OverviewTab lead={lead} />}
            {tab === 'activity' && <ActivityTab leadId={lead.id} />}
            {tab === 'notes' && <NotesTab leadId={lead.id} />}
            {tab === 'email' && <EmailTab leadId={lead.id} leadEmail={lead.email ?? null} />}
          </div>
        )}
      </div>
    </Drawer>
  );
}
